/**
 * Wealthica Data Service
 * 
 * Handles account and position data fetching operations
 * Extracted from client for focused responsibility
 */

import { supabase } from '@/integrations/supabase/client';
import { ensureValidToken } from '@/services/AuthAwareApiService';
import { WealthicaStorage } from '../utils/storage';
import { logger } from '../utils/logger';
import { 
  WealthicaServiceResult, 
  WEALTHICA_ERROR_CODES, 
  createServiceResult, 
  createErrorResult 
} from '../types/errors';

export type AccountsResult = WealthicaServiceResult<any[]>;
export type PositionsResult = WealthicaServiceResult<any[]>;

export class WealthicaDataService {
  /**
   * Get current user ID from Supabase auth
   */
  private static async getCurrentUserId(): Promise<string | null> {
    try {
      const { useAuthStore } = await import('@/stores/authStore');
      const storeUserId = useAuthStore.getState().user?.id;
      if (storeUserId) return storeUserId;
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id || null;
    } catch (error) {
      logger.error('WealthicaDataService', 'Failed to get current user ID', { error });
      return null;
    }
  }

  /**
   * Get Wealthica accounts for the current user
   */
  static async getAccounts(bearerToken?: string): Promise<AccountsResult>;
  static async getAccounts(options: { institutionId: string; providerName: string; bearerToken: string }): Promise<AccountsResult>;
  static async getAccounts(bearerTokenOrOptions?: string | { institutionId: string; providerName: string; bearerToken: string }): Promise<AccountsResult> {
    try {
      let token: string;
      let institutionId: string;
      let providerName: string;

      // Handle both method signatures
      if (typeof bearerTokenOrOptions === 'object') {
        // Direct parameters - used for multi-institution fetching
        token = bearerTokenOrOptions.bearerToken;
        institutionId = bearerTokenOrOptions.institutionId;
        providerName = bearerTokenOrOptions.providerName;
        // Server-only token model: if token missing, acquire one now
        if (!token) {
          const userIdForToken = await this.getCurrentUserId();
          if (!userIdForToken) {
            logger.error('WealthicaDataService', 'No user ID for token retrieval');
            return createServiceResult(false, undefined, 'No user ID for token retrieval', WEALTHICA_ERROR_CODES.AUTH_FAILED);
          }
          const { WealthicaAuthService } = await import('./WealthicaAuthService');
          const tokenResult = await WealthicaAuthService.getToken(userIdForToken);
          token = tokenResult.success && tokenResult.data ? tokenResult.data : '';
        }
      } else {
        // Legacy single-institution method - derive token via Edge Function using userId
        const connectionData = WealthicaStorage.getConnectionData();
        if (!connectionData?.institutionId) {
          logger.error('WealthicaDataService', 'No institution ID available');
          return createServiceResult(false, undefined, 'No institution ID available', WEALTHICA_ERROR_CODES.MISSING_REQUIRED);
        }
        
        institutionId = connectionData.institutionId;
        providerName = connectionData.providerName;
        
        // Acquire token server-side (no frontend storage)
        const userIdForToken = await this.getCurrentUserId();
        if (!userIdForToken) {
          logger.error('WealthicaDataService', 'No user ID for token retrieval');
          return createServiceResult(false, undefined, 'No user ID for token retrieval', WEALTHICA_ERROR_CODES.AUTH_FAILED);
        }
        const { WealthicaAuthService } = await import('./WealthicaAuthService');
        const tokenResult = await WealthicaAuthService.getToken(userIdForToken);
        token = tokenResult.success && tokenResult.data ? tokenResult.data : '';
      }
      
      if (!token) {
        logger.error('WealthicaDataService', 'No bearer token available');
        return createServiceResult(false, undefined, 'No bearer token available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
      }
      
      // Get current user ID
      const userId = await this.getCurrentUserId();
      if (!userId) {
        logger.error('WealthicaDataService', 'No user ID available');
        return createServiceResult(false, undefined, 'No user ID available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
      }
      
      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-accounts');

      logger.info('WealthicaDataService', 'Fetching accounts from edge function', {
        institutionId,
        userId
      });
      
      const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const { data, error } = await supabase.functions.invoke('wealthica2-get-accounts', {
        body: {
          institution_id: institutionId,
          user_id: userId,
          provider_name: providerName,
          bearer_token: token
        },
      });
      const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);

      // Enhanced logging of raw edge function response
      logger.info('WealthicaDataService', 'Raw edge function response', {
        data,
        error,
        hasData: !!data,
        hasError: !!error,
        dataKeys: data ? Object.keys(data) : null,
        dataType: typeof data,
        errorType: typeof error,
        errorKeys: error ? Object.keys(error) : null,
        durationMs,
        cache: (data as any)?.metadata?.cache || 'unknown'
      });

      if (error) {
        logger.error('WealthicaDataService', 'Edge function error', { error });
        return createServiceResult(false, undefined, 'Failed to fetch accounts', error);
      }

      // FIXED: Edge function returns response directly, not wrapped in data property
      // The response is: { wealthicaAccounts: [...], message: "...", status: "success" }
      if (!data?.wealthicaAccounts || !Array.isArray(data.wealthicaAccounts)) {
        logger.warn('WealthicaDataService', 'Invalid accounts response format', {
          hasData: !!data,
          hasWealthicaAccounts: !!data?.wealthicaAccounts,
          wealthicaAccountsType: typeof data?.wealthicaAccounts,
          isArray: Array.isArray(data?.wealthicaAccounts),
          dataKeys: data ? Object.keys(data) : null,
          rawData: data
        });
        return createServiceResult(false, undefined, 'Invalid accounts response format', data);
      }

      logger.info('WealthicaDataService', 'Accounts fetched successfully', {
        accountCount: data.wealthicaAccounts.length,
        institutionId
      });

      // Update last sync timestamp
      if (data.wealthicaAccounts.length > 0) {
        await this.updateLastSyncTimestamp();
      }
      
      return createServiceResult(true, data.wealthicaAccounts);
    } catch (error) {
      logger.error('WealthicaDataService', 'Failed to get accounts', { error });
      return createErrorResult(error);
    }
  }

  /**
   * Get positions for a specific account
   */
  static async getPositionsByAccount(bearerToken: string, accountId: string): Promise<PositionsResult> {
    try {
      if (!accountId) {
        logger.error('WealthicaDataService', 'No account ID provided');
        return createServiceResult(false, undefined, 'No account ID provided', WEALTHICA_ERROR_CODES.MISSING_REQUIRED);
      }
      
      // Get current user ID
      const userId = await this.getCurrentUserId();
      if (!userId) {
        logger.error('WealthicaDataService', 'No user ID available');
        return createServiceResult(false, undefined, 'No user ID available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
      }
      
      // Acquire token if missing (server-only tokens)
      let tokenToUse = bearerToken;
      if (!tokenToUse) {
        const { WealthicaAuthService } = await import('./WealthicaAuthService');
        const tokenResult = await WealthicaAuthService.getToken(userId);
        tokenToUse = tokenResult.success && tokenResult.data ? tokenResult.data : '';
        if (!tokenToUse) {
          return createServiceResult(false, undefined, 'No bearer token available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
        }
      }
      
      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-positions');

      logger.info('WealthicaDataService', 'Fetching positions from edge function', {
        accountId,
        userId
      });
      
      const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const { data, error } = await supabase.functions.invoke('wealthica2-get-positions', {
        body: {
          account_ids: [accountId], // FIXED: Use account_ids array for consistency with edge function
          user_id: userId,
          bearer_token: tokenToUse,
          provider_name: (() => {
            const stored = WealthicaStorage.getConnectionData();
            return stored?.providerName === 'demo' ? 'demo' : 'wealthica';
          })()
        },
      });
      const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
      
      if (error) {
        logger.error('WealthicaDataService', 'Error fetching positions', { error });
        return createServiceResult(false, undefined, error.message, WEALTHICA_ERROR_CODES.API_ERROR);
      }
      
      if (!data?.positions || !Array.isArray(data.positions)) {
        logger.error('WealthicaDataService', 'Invalid positions response', data);
        return createServiceResult(false, undefined, 'Invalid positions response format', WEALTHICA_ERROR_CODES.INVALID_DATA);
      }
      logger.info('WealthicaDataService', 'Positions fetched successfully (by account)', {
        accountId,
        positionCount: data.positions.length,
        durationMs
      });
      
      return createServiceResult(true, data.positions);
    } catch (error) {
      logger.error('WealthicaDataService', 'Failed to get positions by account', { error });
      return createErrorResult(error);
    }
  }

  /**
   * Get positions for a specific institution ID, optionally filtered by account IDs
   */
  static async getPositionsByInstitution(
    bearerToken: string, 
    institutionId: string, 
    accountIds?: string[]
  ): Promise<PositionsResult> {
    try {
      if (!institutionId) {
        logger.error('WealthicaDataService', 'No institution ID provided');
        return createServiceResult(false, undefined, 'No institution ID provided', WEALTHICA_ERROR_CODES.MISSING_REQUIRED);
      }
      
      // Get current user ID
      const userId = await this.getCurrentUserId();
      if (!userId) {
        logger.error('WealthicaDataService', 'No user ID available');
        return createServiceResult(false, undefined, 'No user ID available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
      }
      
      // Acquire token if missing (server-only tokens)
      let tokenToUse = bearerToken;
      if (!tokenToUse) {
        const { WealthicaAuthService } = await import('./WealthicaAuthService');
        const tokenResult = await WealthicaAuthService.getToken(userId);
        tokenToUse = tokenResult.success && tokenResult.data ? tokenResult.data : '';
        if (!tokenToUse) {
          return createServiceResult(false, undefined, 'No bearer token available', WEALTHICA_ERROR_CODES.AUTH_FAILED);
        }
      }
      
      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-positions');

      logger.info('WealthicaDataService', 'Fetching positions from edge function', {
        institutionId,
        accountIdsCount: accountIds?.length || 0,
        userId
      });
      
      // Derive provider name for audit/logging (optional propagation)
      let providerNameForAudit: string = 'wealthica';
      try {
        const { useMultiWealthica2Store } = await import('@/stores/multiWealthica2Store');
        const conn = useMultiWealthica2Store.getState().getConnection?.(institutionId);
        if (conn?.providerName) providerNameForAudit = conn.providerName;
      } catch { /* ignore missing store in some contexts */ }
      if (providerNameForAudit !== 'demo') {
        const stored = WealthicaStorage.getConnectionData();
        if (stored?.providerName === 'demo') providerNameForAudit = 'demo';
      }

      const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const { data, error } = await supabase.functions.invoke('wealthica2-get-positions', {
        body: {
          institutions: institutionId, // FIXED: Edge function expects 'institutions' not 'institution_id'
          user_id: userId,
          bearer_token: tokenToUse,
          account_ids: accountIds || [],
          provider_name: providerNameForAudit === 'demo' ? 'demo' : 'wealthica'
        },
      });
      const durationMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
      
      if (error) {
        logger.error('WealthicaDataService', 'Error fetching positions', { error });
        return createServiceResult(false, undefined, error.message, WEALTHICA_ERROR_CODES.API_ERROR);
      }
      
      if (!data?.positions || !Array.isArray(data.positions)) {
        logger.error('WealthicaDataService', 'Invalid positions response', data);
        return createServiceResult(false, undefined, 'Invalid positions response format', WEALTHICA_ERROR_CODES.INVALID_DATA);
      }
      // Success diagnostics
      logger.info('WealthicaDataService', 'Positions fetched successfully (by institution)', {
        institutionId,
        accountIdsCount: accountIds?.length || 0,
        positionCount: data.positions.length,
        durationMs
      });
      
      return createServiceResult(true, data.positions);
    } catch (error) {
      logger.error('WealthicaDataService', 'Failed to get positions by institution', { error });
      return createErrorResult(error);
    }
  }

  /**
   * Refresh account data (reconnect operation)
   * Now fetches both accounts AND positions for complete data refresh
   */
  static async refreshAccounts(options: { 
    reference: string; 
    institutionId: string 
  }): Promise<{ 
    success: boolean; 
    status?: string; 
    message?: string; 
    data?: { accounts?: any[]; positions?: any[]; institution?: any; } 
  }> {
    try {
      logger.info('WealthicaDataService', 'Starting complete account and position refresh', options);

      // Get current user ID (CRITICAL FIX: was using options.reference incorrectly)
      const userId = await this.getCurrentUserId();
      if (!userId) {
        logger.error('WealthicaDataService', 'No user ID available for refresh');
        throw new Error('No user ID available. Please ensure you are logged in.');
      }

      // Acquire token server-side (no frontend storage)
      const { WealthicaAuthService } = await import('./WealthicaAuthService');
      const tokenResult = await WealthicaAuthService.getToken(userId);
      const bearerToken = tokenResult.success && tokenResult.data ? tokenResult.data : '';
      if (!bearerToken) {
        throw new Error('No bearer token available. Please reconnect your account.');
      }

      // Ensure valid token before making API calls
      await ensureValidToken('wealthica2-reconnect');

      // Step 1: Fetch accounts using dedicated reconnect function
      logger.info('WealthicaDataService', 'Calling wealthica2-reconnect for refresh', {
        institutionId: options.institutionId,
        reference: options.reference
      });

      const accountsResult = await supabase.functions.invoke('wealthica2-reconnect', {
        body: {
          institution_id: options.institutionId,
          reference: options.reference,
          bearer_token: bearerToken
        }
      });

      if (accountsResult.error) {
        logger.error('WealthicaDataService', 'Reconnect function failed', {
          error: accountsResult.error,
          institutionId: options.institutionId
        });
        throw new Error(accountsResult.error.message || 'Failed to refresh account data via reconnect');
      }

      // Extract accounts from reconnect response
      const accounts = accountsResult.data?.data?.accounts || accountsResult.data?.wealthicaAccounts || [];
      
      logger.info('WealthicaDataService', 'Refresh fetched accounts', { 
        accountsCount: accounts?.length || 0,
        rawDataStructure: Object.keys(accountsResult.data || {})
      });

      // Step 2: Fetch positions for all accounts (if we have accounts)
      let positions: any[] = [];
      if (accounts.length > 0) {
        try {
          // Ensure valid token for positions call
          await ensureValidToken('wealthica2-get-positions');

          logger.info('WealthicaDataService', 'Fetching positions for refreshed accounts', {
            institutionId: options.institutionId,
            accountCount: accounts.length
          });

          // Derive provider name for audit/logging (optional propagation)
          let providerNameForAudit: string = 'wealthica';
          try {
            const { useMultiWealthica2Store } = await import('@/stores/multiWealthica2Store');
            const conn = useMultiWealthica2Store.getState().getConnection?.(options.institutionId);
            if (conn?.providerName) providerNameForAudit = conn.providerName;
          } catch { /* ignore */ }
          if (providerNameForAudit !== 'demo') {
            const stored = WealthicaStorage.getConnectionData();
            if (stored?.providerName === 'demo') providerNameForAudit = 'demo';
          }

          const positionsResult = await supabase.functions.invoke('wealthica2-get-positions', {
            body: {
              institutions: options.institutionId, // FIXED: Edge function expects 'institutions'
              user_id: userId, // CRITICAL FIX: Use actual user ID, not options.reference
              bearer_token: bearerToken,
              account_ids: [], // Empty array means fetch for all accounts
              provider_name: providerNameForAudit === 'demo' ? 'demo' : 'wealthica'
            }
          });

          if (positionsResult.error) {
            logger.warn('WealthicaDataService', 'Failed to fetch positions during refresh, continuing with accounts only', { 
              error: positionsResult.error 
            });
            // Don't throw - accounts refresh succeeded, positions are optional
          } else {
            positions = positionsResult.data?.positions || [];
            logger.info('WealthicaDataService', 'Refresh fetched positions', { 
              positionCount: positions.length 
            });
          }
        } catch (positionError) {
          logger.warn('WealthicaDataService', 'Position fetch failed during refresh, continuing with accounts only', { 
            error: positionError 
          });
          // Don't throw - accounts refresh succeeded, positions are optional
        }
      } else {
        logger.info('WealthicaDataService', 'No accounts found, skipping position fetch');
      }

      // Update last sync timestamp on any successful refresh (data freshness indicator)
      await this.updateLastSyncTimestamp();
      logger.info('WealthicaDataService', 'Updated lastSyncAt after successful refresh');

      // Return in the expected format with both accounts and positions
      return {
        success: true,
        status: 'completed',
        message: `Account refresh completed successfully (${accounts.length} accounts, ${positions.length} positions)`,
        data: {
          accounts: accounts,
          positions: positions, // Now includes actual position data
          institution: null
        }
      };
    } catch (error) {
      logger.error('WealthicaDataService', 'Account refresh failed', { 
        error,
        institutionId: options.institutionId,
        reference: options.reference
      });

      // Enhanced error handling with cleanup
      try {
        // Clear any corrupted cache data on refresh failure
        WealthicaStorage.clearInstitutionCache(options.institutionId);
        logger.info('WealthicaDataService', 'Cleared corrupted institution cache after refresh failure');
      } catch (cleanupError) {
        logger.warn('WealthicaDataService', 'Cache cleanup failed', { cleanupError });
      }

      // Return structured error instead of throwing for better error boundary handling
      return {
        success: false,
        status: 'failed',
        message: error instanceof Error ? error.message : 'Account refresh failed',
        data: { accounts: [], positions: [], institution: null }
      };
    }
  }

  /**
   * Update last sync timestamp in connection data
   */
  private static async updateLastSyncTimestamp(): Promise<void> {
    try {
      const connectionData = WealthicaStorage.getConnectionData();
      if (connectionData) {
        connectionData.lastSyncAt = new Date().toISOString();
        WealthicaStorage.setConnectionData(connectionData);
        logger.info('WealthicaDataService', 'Updated connection lastSyncAt', { 
          timestamp: connectionData.lastSyncAt 
        });
      }
    } catch (error) {
      logger.error('WealthicaDataService', 'Failed to update last sync timestamp', { error });
      // Don't throw - this is not critical
    }
  }
}